interface Props {
  type: string;
  size?: number;
  color?: string;
}

/** Miniaturas de face frontal (arte do operador, 2026-08-23) — só CCS2 e
 * Type 2, que é a frota real. Tipo sem arte cai no desenho SVG abaixo.
 *
 * São máscaras de alfa, não imagens: a cor continua vindo de `color`, o que
 * preserva o uso em `ConnectorAvailPills` (verde = há conector livre, cinza =
 * ocupado). Um `<img>` perderia esse estado, que é informação, não enfeite.
 *
 * Estas são a versão CHAPADA do plugue. A arte em perspectiva (`ConnectorArt`)
 * fica só nos 40–76px do card de seleção: em 11–19px ela vira borrão.
 */
const GLYPH_ART: Record<string, string> = {
  CCS2: '/connectors/ccs2-glyph.png',
  Type2: '/connectors/type2-glyph.png'
};

/** Símbolo do tipo de conector (CCS2 / CHAdeMO / Type2 / GBT), monocromático
 * e centrado num viewBox 24×24 — portado 1:1 do Claude Design
 * (mapfirst-core.jsx · `ConnectorGlyph`). O antigo `CCS2Icon` (usado nos
 * tiles do resumo por tipo/linhas de conector) renderizava sempre o mesmo
 * desenho de CCS2 independente do `type` real — bug reportado Saulo
 * 2026-07-08 (Type2 aparecia com o ícone do CCS2). */
export function ConnectorGlyph({ type = 'CCS2', size = 20, color = 'currentColor' }: Props) {
  const art = GLYPH_ART[(type ?? '').trim()];
  if (art) {
    return (
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: size,
          height: size,
          backgroundColor: color,
          WebkitMaskImage: `url("${art}")`,
          maskImage: `url("${art}")`,
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center'
        }}
      />
    );
  }

  const dot = (cx: number, cy: number, r: number, key: string) => (
    <circle key={key} cx={cx} cy={cy} r={r} fill={color} stroke="none" />
  );
  const stroke = { stroke: color, strokeWidth: 1.4, fill: 'none' } as const;

  let body: JSX.Element;
  if (type === 'CCS2') {
    body = (
      <>
        <path d="M8.2 4.6 L15.8 4.6 A4.4 4.4 0 1 1 8.2 4.6 Z" {...stroke} />
        {dot(10.7, 5.6, 0.6, 'a')}
        {dot(13.3, 5.6, 0.6, 'b')}
        {dot(9.6, 7.4, 0.85, 'c')}
        {dot(14.4, 7.4, 0.85, 'd')}
        {dot(12, 7.6, 0.8, 'e')}
        <path d="M6.6 14 a1.9 1.9 0 0 1 1.9-1.9 h7 a1.9 1.9 0 0 1 1.9 1.9 v3.4 a1.9 1.9 0 0 1 -1.9 1.9 h-7 a1.9 1.9 0 0 1 -1.9-1.9 z" {...stroke} />
        {dot(9.4, 15.7, 1.55, 'f')}
        {dot(14.6, 15.7, 1.55, 'g')}
      </>
    );
  } else if (type === 'CHAdeMO') {
    body = (
      <>
        <circle cx="12" cy="12" r="8.5" {...stroke} />
        {dot(12, 12, 1.4, 'a')}
        {dot(12, 6.5, 1.2, 'b')}
        {dot(12, 17.5, 1.2, 'c')}
        {dot(6.5, 12, 1.2, 'd')}
        {dot(17.5, 12, 1.2, 'e')}
        {dot(8.3, 8.3, 1, 'f')}
        {dot(15.7, 8.3, 1, 'g')}
        {dot(8.3, 15.7, 1, 'h')}
        {dot(15.7, 15.7, 1, 'i')}
      </>
    );
  } else if (type === 'GBT' || type === 'GB/T') {
    body = (
      <>
        <circle cx="12" cy="12" r="8.5" {...stroke} />
        {dot(12, 7, 1.2, 'a')}
        {dot(7.5, 10, 1.1, 'b')}
        {dot(16.5, 10, 1.1, 'c')}
        {dot(9.5, 15.5, 1.1, 'd')}
        {dot(14.5, 15.5, 1.1, 'e')}
        {dot(12, 12, 1.2, 'f')}
      </>
    );
  } else {
    // Type2 (Mennekes) — default
    body = (
      <>
        <path d="M5.6 8.4 L18.4 8.4 A6.6 6.6 0 1 1 5.6 8.4 Z" {...stroke} />
        {dot(10.5, 9.7, 0.72, 'a')}
        {dot(13.5, 9.7, 0.72, 'b')}
        {dot(8.4, 12.6, 1.3, 'c')}
        {dot(15.6, 12.6, 1.3, 'd')}
        {dot(12, 12.6, 1.15, 'e')}
        {dot(9.9, 15.7, 1.05, 'f')}
        {dot(14.1, 15.7, 1.05, 'g')}
      </>
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      {body}
    </svg>
  );
}
